// 引入 React 的钩子和组件
import {
  useEffect,
  useState,
} from 'react';

// 引入 Ant Design 的组件
import {
  Input,
  InputNumber,
  InputNumberProps,
  Modal,
  Popover,
  Switch,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';

// 引入自定义组件和资源
import Icon from '@/components/Icon';
import ModalFoot from '@/components/ModalFoot';
import doctor from '@/image/doctor.svg';
import question from '@/image/question.svg';
import settingIcon from '@/image/settingIcon.svg';
import useTransactionSetting, {
  maxSlippage,
  maxSlippageExpert,
  slippageArr,
} from '@/store/transactionSetting';
import { formatTo6Decimals } from '@/utils';

// 定义组件的类型
type ComType = {
    type: 0 | 1 // 0 表示交换设置，1 表示流动性设置
};

// 定义确认文本
const confirm = 'confirm';

// 定义弹窗内容子组件
const ContentChildren = ({ text, className }: { text: string, className?: string }) => {
    return (
        <div className={`w-45 text-66 text-xs ${className}`}>
            {text}
        </div>
    );
};

// 定义设置内容组件
const Content = ({ type }: ComType) => {
    const { t } = useTranslation()

    // 从全局状态中获取交易设置和更新方法
    const { transactionSetting: { swapSlippage, swapSpecialist, swapTradingHour, mobilitySlippage, mobilitySpecialist, mobilityTradingHour },
        setSwapSlippage, setSwapTradingHour, setSwapSpecialist, setMobilitySlippage, setMobilityTradingHour, setMobilitySpecialist } = useTransactionSetting();

    // 根据类型选择对应的设置
    const slippage = [swapSlippage, mobilitySlippage][type]; // 滑点
    const specialist = [swapSpecialist, mobilitySpecialist][type]; // 专家模式
    const tradingHour = [swapTradingHour, mobilityTradingHour][type]; // 交易时间
    const setSlippage = [setSwapSlippage, setMobilitySlippage][type]; // 设置滑点的方法
    const setTradingHour = [setSwapTradingHour, setMobilityTradingHour][type]; // 设置交易时间的方法
    const setSpecialist = [setSwapSpecialist, setMobilitySpecialist][type]; // 设置专家模式的方法

    // 定义本地状态
    const [slippageState, setSlippageState] = useState(''); // 滑点输入框的值
    const [tradingHourState, setTradingHourState] = useState(''); // 交易时间输入框的值
    const [open, setOpen] = useState(false); // 弹窗的打开状态
    const [isConfirm, setIsConfirm] = useState(false); // 是否显示确认模式
    const [confirmInput, setConfirmInput] = useState(''); // 确认输入框的值
    const confirmOk = confirm === confirmInput; // 确认输入是否正确

    // 滑点输入框的变化事件
    const onChangeSlippage: InputNumberProps['onChange'] = (value) => {
        const value2 = formatTo6Decimals(value || '', 2)
        setSlippageState(value2);
    };

    // 滑点输入框失焦事件
    const onBlurSlippage = () => {
        if (slippageState) {
            setSlippage(slippageState); // 更新滑点
        } else {
            setSlippageState('0.01');
            setSlippage('0.01')
        }
    };

    // 交易时间输入框的变化事件
    const onChangeTradingHour: InputNumberProps['onChange'] = (value) => {
        const value2 = formatTo6Decimals(value || '', 18)
        setTradingHourState(value2);
    };

    // 交易时间输入框失焦事件
    const onBlurTradingHour = () => {
        if (tradingHourState) {
            setTradingHour(tradingHourState); // 更新交易时间
        } else {
            setTradingHourState('0.00001');
            setTradingHour('0.00001')
        }
    };

    // 专家模式切换事件
    const onChangeSpecialist = (checked: boolean) => {
        if (!specialist) {
            showModal(); // 如果未开启专家模式，显示确认弹窗
        } else {
            setSpecialist(!specialist); // 切换专家模式
        }
    };

    // 确认弹窗的确认按钮事件
    const handleOkConfirm = () => {
        if (confirmOk) {
            setSpecialist(true); // 启用专家模式
            setOpen(false); // 关闭弹窗
            setIsConfirm(false);
            setConfirmInput('');
        }
    };

    // 显示弹窗
    const showModal = () => {
        setOpen(true);
    };

    // 弹窗的确认按钮事件
    const handleOk = () => {
        setIsConfirm(true); // 显示确认模式
    };

    // 弹窗的取消按钮事件
    const handleCancel = () => {
        setOpen(false); // 关闭弹窗
        setIsConfirm(false);
        setConfirmInput('');
    };

    // 初始化滑点状态
    useEffect(() => {
        setSlippageState(slippage);
    }, [slippage]);

    // 初始化交易时间状态
    useEffect(() => {
        setTradingHourState(tradingHour);
    }, [tradingHour]);

    return (
        <div className='w-72'>
            {/* 滑点上限设置 */}
            <div className='flex items-center text-mainBlack text-sm mb-2.25'>{t('com.slippageTolerance')}
                <Popover placement="bottom" content={<ContentChildren text={t('com.slippageWarning')} />}>
                    <div>
                        <Icon src={question} className='w-4.5 ml-1.5 text-a6 cursor-pointer' />
                    </div>
                </Popover>
            </div>
            <div className='flex items-center justify-between text-mainBlack mb-3.75'>
                <div className='border border-ee px-1.5 h-7.5 flex items-center justify-center rounded-5'>
                    <div onClick={() => setSlippage(type ? slippageArr[0] : slippageArr[1])} className='rounded-5 text-xs mr-2 bg-ee h-5 w-9.5 flex items-center justify-center cursor-pointer'>{t('com.auto')}</div>
                    <InputNumber stringMode value={slippageState} suffix="%" min='0.01' className='max-w-10' controls={false} max={specialist ? maxSlippageExpert : maxSlippage} onChange={onChangeSlippage} onBlur={onBlurSlippage} />
                </div>
                {
                    slippageArr.map((item) => {
                        return <div onClick={() => { setSlippage(item) }} key={item} className={`${slippage === item ? 'bg-e2' : ''} border cursor-pointer border-ee w-14.5 h-7.5 flex items-center justify-center rounded-5`}> {item}%</div>
                    })
                }
            </div>

            {/* 交易截止日期设置 */}
            <div className='flex items-center text-mainBlack text-sm mb-2.25'>{t('com.transactionDeadline')}
                <Popover placement="bottom" content={<ContentChildren text={t('com.transactionDeadlineDescription')} />}>
                    <div>
                        <Icon src={question} className='w-4.5 ml-1.5 text-a6 cursor-pointer' />
                    </div>
                </Popover>
            </div>
            <div className='flex items-center mb-3.75'>
                <div className='border border-ee px-1.5 h-7.5 flex items-center justify-center rounded-5 mr-1.25'>
                    <InputNumber value={tradingHourState} onChange={onChangeTradingHour} onBlur={onBlurTradingHour} stringMode className='max-w-14 center' controls={false} max={'4320'} min='0.00001' />
                </div>
                {t('com.minutes')}
            </div>

            {/* 专家模式切换 */}
            <div className='flex items-center text-mainBlack text-sm mb-2.25'>{t('com.toggleExpertMode')}
                <Popover placement="bottom" content={<ContentChildren text={t('com.expertModeDescription')} />}>
                    <div className='mr-5'>
                        <Icon src={question} className='w-4.5 ml-1.5 text-a6 cursor-pointer' />
                    </div>
                </Popover>
                <Switch value={specialist} onChange={onChangeSpecialist} />
            </div>

            {/* 专家模式确认弹窗 */}
            <Modal
                centered
                keyboard
                closeIcon={false}
                open={open}
                onCancel={handleCancel}
                footer={null}
                className='w-75.5'
            >
                {
                    isConfirm ?
                        <div className='px-5  pt-4'>
                            <p className='text-86 text-sm pb-2.5'>{t('com.pleaseEnter')}“<span className='text-mainBlack'>{confirm}</span>”{t('com.enterToEnableExpertMode')}</p>
                            <Input placeholder={`${t('com.pleaseEnter')}...`} onChange={(e) => { setConfirmInput(e.target.value) }} value={confirmInput} className='mb-6.5' />
                        </div>
                        : <div className='text-mainBlack mb-5 px-6.25  pt-4'>
                            <div className='font-medium text-lg pb-5 text-center'>{t('com.areYouSure')}</div>
                            <div className='text-86 text-sm'>
                                <p>{t('com.expertModeWarning')}</p>
                                <p>{t('com.useExpertModeWarning')}</p>
                            </div>
                        </div>
                }
                <ModalFoot handleCancel={handleCancel} handleOk={isConfirm ? handleOkConfirm : handleOk} classname={`${isConfirm ? (confirmOk ? 'text-mainBlack' : 'text-gray-300') : 'text-mainBlack'}`} />
            </Modal>
        </div>
    );
};

// 定义设置组件
const Setting = ({ type }: ComType) => {
    const { transactionSetting: { swapSlippage, swapSpecialist, mobilitySlippage, mobilitySpecialist } } = useTransactionSetting();
    const slippage = [swapSlippage, mobilitySlippage][type]; // 当前滑点
    const specialist = [swapSpecialist, mobilitySpecialist][type]; // 当前专家模式状态

    return (
        <Popover placement="bottomRight" content={<Content type={type} />} >
            <div className='flex items-center justify-self-end cursor-pointer' >
                <div className='rounded-5 bg-white px-2.5 h-7.5 flex items-center justify-center'>
                    <span className='text-xs text-mainBlack'>{slippage}%</span>
                    <div className='relative'>
                        <Icon src={settingIcon} className='w-6 ml-1.5' />
                        <ReactSVG src={doctor} className={`w-4.5 absolute -right-1  top-2 ${specialist ? '' : 'hidden'} `} />
                    </div>
                </div>
            </div>
        </Popover>
    );
};

export default Setting;